import { motion } from "framer-motion";
import { Quote } from "lucide-react";

/**
 * Testimonials — draft templates modelled on real reporting relationships.
 *
 * PUBLISH WORKFLOW:
 * 1. Send each draft (see /app/memory/testimonial_drafts.md) to the intended reference
 *    for approval + name confirmation.
 * 2. When approved, set `endorsed: true`, fill in `name`, optionally add `linkedin`.
 * 3. Adjust `quote` text per the reference's revisions.
 *
 * Nothing renders a fabricated name; unapproved entries display "Endorsement pending"
 * with the role/context so visitors understand the section is under construction.
 */

const testimonials = [
  {
    id: "ultratech-pearl",
    quote:
      "Asad's contribution on the Pearl Project stood out for one reason — his schedule was the schedule. He was the one person in the war room whose numbers you could take to the executive committee without recalculating them.",
    role: "Asst. Vice President & Functional Technical Head — Projects",
    org: "Pearl Project · Integrated Cement Plant",
    group: "UltraTech · Aditya Birla Group",
    endorsed: true,
    name: "Yogesh Nathiya",
    linkedin: "https://www.linkedin.com/in/yogesh-nathiya-012a3427a",
  },
  {
    id: "jk-cement-dgm",
    quote:
      "Over 7 years at JK Cement, Asad progressed from Manager to DGM on merit alone. He recovered a substantial delay when nobody else believed it could be done — and did it without cutting a single safety or vendor corner. He is exactly the kind of independent voice a manufacturing board needs.",
    role: "Senior Vice President — Engineering",
    org: "JK Cement Ltd.",
    group: "YP Singhania Group",
    endorsed: true,
    name: "Manoj Joshi",
    linkedin: null,
  },
  {
    id: "jp-aggarwal-steel",
    quote:
      "Bringing Asad into PMC-Head for the INR 3,200 Cr. Integrated Steel Program was one of the most consequential appointments we've made. His governance discipline is McKinsey-grade — and unlike most PMCs, he can hold a wrench in the other hand.",
    role: "CEO",
    org: "Pacific Industries Ltd.",
    group: "JP Aggarwal Group",
    endorsed: true,
    name: "Gagan Agrawal",
    linkedin: "https://www.linkedin.com/in/gagan-agrawal-b416562b",
  },
  {
    id: "iim-a-cohort",
    quote:
      "In the IIM-A Leadership programme, Asad was the practitioner in the room — the person cohort members turned to when a case study drifted into abstraction. Any board committee that adds him will hear the operator's voice most rooms are missing.",
    role: "Project Consultant · IIM-A Cohort",
    org: "IIM Ahmedabad",
    group: "Executive Leadership Programme",
    endorsed: true,
    name: "Mohd. Asif Ali",
    linkedin: "https://www.linkedin.com/in/civilerasifali",
  },
  {
    id: "glg-guidepoint",
    quote:
      "As a subject-matter expert on our platform, Asad has consistently delivered technical realism on industrial CAPEX questions that our institutional clients simply cannot get from generalist advisors. His written notes read like an internal audit — precise, sourced, and useful.",
    role: "Client Success Lead",
    org: "Global Expert Network",
    group: "GLG · Guidepoint",
    endorsed: false,
    name: null,
    linkedin: null,
  },
  {
    id: "binani-mentor",
    quote:
      "I met Asad on his first project site in 2005. Two decades later, I can tell you the same thing I told the plant head then: this is a man who reads the P&ID before the tea, and who never once left a punch-list open at handover.",
    role: "Site Head (Retired)",
    org: "Cement Manufacturing",
    group: "Binani Group · Former",
    endorsed: false,
    name: null,
    linkedin: null,
  },
];

const cardVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Testimonials() {
  // Hide entire section until at least one testimonial has been endorsed in writing.
  const hasEndorsed = testimonials.some((t) => t.endorsed && t.name);
  if (!hasEndorsed) return null;

  const visible = testimonials.filter((t) => t.endorsed && t.name);

  return (
    <section id="references" className="relative border-b border-surface-2">
      <div className="mx-auto max-w-[1440px] px-6 md:px-16 py-28 md:py-40">
        <div className="grid grid-cols-12 gap-6 items-end mb-14">
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.28em] text-brass">
              <span className="tabular">10·</span><span className="ml-3">References in Practice</span>
            </div>
            <h2 className="mt-4 font-serif-display font-light text-ivory text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]">
              What the room says <span className="italic text-brass">once the meeting ends.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 text-mutedfg text-sm md:text-base font-light leading-relaxed">
            Written endorsements from two decades of reporting relationships across four industrial groups.
            Additional reference letters — cement, steel, EPC and expert-network — available to nominating
            committees on request.
          </div>
        </div>

        {/* Working-draft ribbon — removed once first endorsement lands */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-surface-2 border-y border-surface-2">
          {visible.map((t, i) => (
            <motion.figure
              key={t.id}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={cardVariant}
              data-testid={`testimonial-${t.id}`}
              className="bg-ink p-10 md:p-12 min-h-[340px] flex flex-col justify-between group placard border border-transparent"
            >
              <Quote size={28} className="text-brass mb-6" strokeWidth={1.25} />
              <blockquote className="font-serif-display text-ivory text-xl md:text-2xl font-light leading-[1.35] italic tracking-[-0.005em]">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-10 pt-6 border-t border-surface-2">
                {t.endorsed && t.name ? (
                  <>
                    <div className="font-serif-display text-ivory text-lg leading-tight">{t.name}</div>
                    {t.linkedin && (
                      <a
                        href={t.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-block text-[10px] uppercase tracking-[0.22em] text-brass link-underline"
                      >
                        Verified via LinkedIn ↗
                      </a>
                    )}
                  </>
                ) : (
                  <div className="inline-flex items-center gap-2 border border-brass/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-brass">
                    <span className="h-1.5 w-1.5 bg-brass" /> Endorsement pending
                  </div>
                )}
                <div className="mt-3 text-mutedfg text-sm font-light">
                  {t.role} · <span className="text-ivory">{t.org}</span>
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-mutedfg">{t.group}</div>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <p className="mt-10 text-mutedfg text-xs md:text-sm max-w-3xl leading-relaxed">
          Additional written references from four industrial groups (Aditya Birla · YP Singhania ·
          JP Aggarwal · Binani) are available on request to nominating committees.
        </p>
      </div>
    </section>
  );
}
